import { useQuery } from '@apollo/client';
import { ESubscriptionDetailStatus, ESubscriptionTier } from '__generated__/globalTypes';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import { CurrentUser } from 'graphql/query/__generated__/CurrentUser';
import { CURRENT_USER_QUERY } from 'graphql/query/userQuery';
import { EColors, EShadows } from 'libs/styles/styling';
import { useContext, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import { GlobalContext } from 'session/state/context';
import { apiSubscriptionCreatePortalLink } from 'static/api/directApi';
import { SUBSCRIPTION_CONFIG } from 'static/config/subscriptionConfig';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const SubscriptionPageContent = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const {
        state: { loggedUser }
    } = useContext(GlobalContext);
    const query = useQuery<CurrentUser>(CURRENT_USER_QUERY);

    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(false);

    const user = query.data?.currentUser;

    if (!user) {
        return null;
    }

    const getManageSubscriptionButton = (params: {
        text: string;
        type: 'primary' | 'secondary';
    }) => {
        return (
            <S_WebButton
                type={params.type}
                text={params.text}
                loading={isSubscriptionLoading}
                onClick={async () => {
                    setIsSubscriptionLoading(true);
                    const response = await apiSubscriptionCreatePortalLink();
                    const data = response.data as { redirectUrl: string };

                    window.location.href = data.redirectUrl;
                }}
                shadow={EShadows.LIGHT}
            />
        );
    };

    const getOpenModalButton = ({
        text,
        type
    }: {
        text: string;
        type: 'primary' | 'secondary';
    }) => {
        return (
            <S_WebButton
                type={type}
                text={text}
                onClick={() => {
                    ctxModal.setModal({
                        type: EModal.UPGRADE
                    });
                }}
                shadow={EShadows.LIGHT}
            />
        );
    };
    return (
        <S_SubscriptionPageContent className={props.className}>
            <S_Block>
                <S_SubscriptionInfo>
                    {Localization.formatString(
                        Localization.YOU_HAVE_NAME,
                        SUBSCRIPTION_CONFIG[loggedUser.subscriptionTier ?? ESubscriptionTier.FREE]
                            .name
                    )}
                </S_SubscriptionInfo>
                {!user.subscriptionDetailStatus && (
                    <S_Buttons>
                        {getOpenModalButton({
                            type: 'primary',
                            text: Localization.SUBSCRIBE_ACTION
                        })}
                    </S_Buttons>
                )}
                {user.subscriptionDetailStatus === ESubscriptionDetailStatus.ACTIVE && (
                    <S_Buttons>
                        {getOpenModalButton({
                            type: 'primary',
                            text: Localization.MANAGE_SUBSCRIPTION
                        })}
                    </S_Buttons>
                )}
                {user.subscriptionDetailStatus === ESubscriptionDetailStatus.CANCEL_SCHEDULED && (
                    <>
                        <S_SubscriptionInfo>
                            {Localization.CANCELLED_SUBSCRIPTION}
                        </S_SubscriptionInfo>
                        <S_Buttons>
                            {getManageSubscriptionButton({
                                type: 'primary',
                                text: Localization.RENEW_SUBSCRIPTION
                            })}
                        </S_Buttons>
                    </>
                )}
                {user.subscriptionDetailStatus === ESubscriptionDetailStatus.CANCELED && (
                    <S_Buttons>
                        {getOpenModalButton({
                            type: 'primary',
                            text: Localization.RESUBSCRIBE
                        })}
                        {getManageSubscriptionButton({
                            type: 'secondary',
                            text: Localization.VIEW_PAST_INVOICES
                        })}
                    </S_Buttons>
                )}

                {user.subscriptionDetailStatus === ESubscriptionDetailStatus.PAYMENT_REQUIRED && (
                    <>
                        <S_SubscriptionInfo>{Localization.SUBSCRIBE_NOT_PAID}</S_SubscriptionInfo>
                        <S_Buttons>
                            {getManageSubscriptionButton({
                                type: 'primary',
                                text: Localization.REVIEW_PAYMENT
                            })}
                        </S_Buttons>
                    </>
                )}
            </S_Block>
        </S_SubscriptionPageContent>
    );
};

const S_SubscriptionPageContent = styled.div`
    display: flex;
`;

const S_SubscriptionInfo = styled.div`
    color: ${EColors.DARK};
    margin-bottom: 20px;
`;

const S_Block = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Buttons = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    column-gap: 15px;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 0 0 15px 0;
`;
